import { useEffect, useState } from 'react';
import { sanitizeValue } from '../../lib/sanitize';
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { useSort } from '../useSort';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addAccountSite'
  | 'getAccountSite'
  | 'getAccountSites'
  | 'updateAccountSite';

export const useAccountSites = () => {
  const [action, setAction] = useState<Action>('');
  const [accountSite, setAccountSite] = useState<AccountSite | null>(null);
  const [accountSites, setAccountSites] = useState<AccountSite[]>([]);
  const { loading, makeRequest, response } = useFetch();
  const {
    id: operatorId,
    sites: operatorAccountSites,
    updateOperatorState,
  } = useOperator();
  const { sortAsc } = useSort<{ name: string }>('name');
  const BASE_URL = `${BASE_URL_MAP['Hospitality']}/${operatorId}/accountSites`;

  useEffect(() => {
    resetState();
  }, [operatorId]);

  useEffect(() => {
    if (response && !loading) {
      if (action === 'updateAccountSite') {
        const mappedAccountSite = mapAccountSite(response);
        updateOperatorState(
          'sites',
          [
            mappedAccountSite,
            ...(operatorAccountSites ?? []).filter(
              (accountSite) => accountSite.id !== mappedAccountSite.id
            ),
          ].sort(sortAsc)
        );
      } else if (action === 'addAccountSite') {
        const mappedAccountSite = mapAccountSite(response);

        updateOperatorState(
          'sites',
          [mappedAccountSite, ...(operatorAccountSites ?? [])].sort(sortAsc)
        );
      } else if (action === 'getAccountSite')
        setAccountSite(mapAccountSite(response));
      else if (action === 'getAccountSites')
        setAccountSites(
          response.map((accountSite: ApiAccountSite) =>
            mapAccountSite(accountSite)
          )
        );
    }
    // eslint-disable-next-line
  }, [loading, response]);

  const addAccountSite = (accountSite: CreateAccountSite) => {
    setAction('addAccountSite');
    const body = mapApiAccountSite(accountSite);

    makeRequest(BASE_URL, 'post', sanitizeValue(body));
  };

  const getAccountSite = (accountSiteId: string) => {
    setAction('getAccountSite');
    makeRequest(`${BASE_URL}/${accountSiteId}`, 'get');
  };

  const getAccountSites = () => {
    setAction('getAccountSites');
    makeRequest(BASE_URL, 'get');
  };

  const updateAccountSite = (accountSite: AccountSite) => {
    setAction('updateAccountSite');
    const body: ApiCreateAccountSite = {
      Active: accountSite.active,
      BacCode: accountSite.abbr,
      Name: accountSite.name,
      SiteCode: accountSite.code,
    };

    makeRequest(`${BASE_URL}/${accountSite.id}`, 'put', sanitizeValue(body));
  };

  const resetState = () => {
    setAction('');
    setAccountSite(null);
    setAccountSites([]);
  };

  return {
    addAccountSite,
    accountSite,
    accountSites,
    getAccountSite,
    getAccountSites,
    loading,
    updateAccountSite,
  };
};

const mapApiAccountSite = (
  accountSite: CreateAccountSite
): ApiCreateAccountSite => ({
  Active: accountSite.active,
  BacCode: accountSite.abbr,
  Name: accountSite.name,
  SiteCode: accountSite.code.toString(),
});

export const mapAccountSite = (accountSite: ApiAccountSite): AccountSite => ({
  id: accountSite.ID,
  abbr: accountSite.BacCode,
  active: accountSite.Active,
  code: accountSite.SiteCode,
  name: accountSite.Name,
});
